import { useEffect, useRef, useState } from 'react';

import { getCurrentTimestamp } from '../../utils/DateUtils';
import { PROGRESS_TO_PENDING_DELAY } from './helper';

/**
 * The `progress` → `pending` countdown.
 *
 * Ported from `WithdrawStatusFragment.setCountdown`
 * (`payments/ui/fragments/WithdrawStatusFragment.kt:1879-1910`). Android ticks
 * once a second for `processingDelay ?: PROGRESS_TO_PENDING_DELAY` seconds,
 * showing the remainder as "N sec", and on expiry rewrites the status to
 * `pending` **locally** — no request is made, because the server has nothing new
 * to say until it pushes `REFRESH_WITHDRAW_STATUS_SCREEN`.
 *
 * `onExpire` fires at most once per run. A fresh screen arriving mid-countdown
 * changes `active`/`seconds`, which restarts the timer and re-arms the callback —
 * Android's `isTransitioningToPending = false` on every new status screen.
 */

interface IUseProcessingCountdown {
  /** True only while the rendered status is `progress`. */
  active: boolean;
  /** `processingDelay` from the state payload; falls back to Android's 10s. */
  seconds?: number;
  onExpire: () => void;
}

export default function useProcessingCountdown({
  active,
  seconds,
  onExpire,
}: IUseProcessingCountdown): number | undefined {
  const total = seconds ?? PROGRESS_TO_PENDING_DELAY;
  const [remaining, setRemaining] = useState<number | undefined>(
    active ? total : undefined,
  );

  // Kept in a ref so a new callback identity does not restart the countdown.
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    if (!active) {
      setRemaining(undefined);
      return;
    }

    setRemaining(total);

    let countDown = total;
    const timer = setInterval(() => {
      countDown -= 1;
      setRemaining(countDown);

      if (countDown <= 0) {
        clearInterval(timer);
        onExpireRef.current();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [active, total]);

  return remaining;
}

/* ------------------------------------------------------------------ */

interface IUseDeadlineCountdown {
  /** Absolute epoch-ms the payout is expected to settle by. */
  deadline?: number;
  /** Stops ticking once the status is terminal. */
  active: boolean;
  onExpire?: () => void;
}

/**
 * Ticks once a second toward an absolute deadline, for the server's
 * `estimatedProcessingTs`.
 *
 * The screen pairs a sentence ending in "in " with this value, the way Android
 * pairs `tvTimerText` with `tvTimer`
 * (`res/layout/withdrawal_bank_progress_card.xml:29-56`) and
 * `LeaveIntentAdapter` appends `"${text} ${timer}"`
 * (`payments/ui/adapter/LeaveIntentAdapter.kt:167-183`). `onExpire` mirrors that
 * adapter's `onFinish { refresh.invoke() }` — when the estimate runs out, ask the
 * server rather than leave a dead "00s" on screen.
 *
 * Returns the milliseconds left, or `undefined` when there is nothing to show:
 * no deadline, a non-positive one, or one already in the past. Callers render
 * the sentence alone in that case, never a dangling "in".
 */
export function useDeadlineCountdown({
  deadline,
  active,
  onExpire,
}: IUseDeadlineCountdown): number | undefined {
  const hasDeadline = Boolean(deadline && deadline > 0);

  const [remaining, setRemaining] = useState<number | undefined>(() =>
    hasDeadline ? (deadline as number) - getCurrentTimestamp() : undefined,
  );

  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    if (!active || !hasDeadline) {
      setRemaining(undefined);
      return;
    }

    const tick = () => (deadline as number) - getCurrentTimestamp();

    const left = tick();
    setRemaining(left);
    if (left <= 0) return;

    const timer = setInterval(() => {
      const next = tick();
      setRemaining(next);

      if (next <= 0) {
        clearInterval(timer);
        onExpireRef.current?.();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [active, hasDeadline, deadline]);

  return remaining !== undefined && remaining > 0 ? remaining : undefined;
}
